import { format } from "date-fns";
import { FC, useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaCalendarAlt } from "react-icons/fa";
import { RxCaretDown } from "react-icons/rx";


interface DateRangeFilterProps {
    /** Preselected start date, if any */
    initialStartDate?: Date | null;
    /** Preselected end date, if any */
    initialEndDate?: Date | null;
    /** Called when user hits "Apply" or clears the date range */
    onChange?: (start: Date | null, end: Date | null) => void;
    /** Button label when no dates selected */
    placeholder?: string;
}

/**
 * A date range picker with input fields for start and end dates
 * featuring auto-formatting, improved UI, and validation.
 */
export const DateFilter: FC<DateRangeFilterProps> = ({
    initialStartDate = null,
    initialEndDate = null,
    onChange,
    placeholder = "Dates",
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [tempStartDate, setTempStartDate] = useState<Date | null>(initialStartDate);
    const [tempEndDate, setTempEndDate] = useState<Date | null>(initialEndDate);
    const [error, setError] = useState<string | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Add CSS for datepicker styling
    useEffect(() => {
        // Add custom styles for the datepicker
        const style = document.createElement('style');
        style.innerHTML = `
            .react-datepicker__day--selected, 
            .react-datepicker__day--in-selecting-range, 
            .react-datepicker__day--in-range {
                background-color: #2563eb !important;
            }
            
            .react-datepicker-popper {
                z-index: 40 !important;
            }
            
            .react-datepicker {
                font-family: inherit;
                border-radius: 0.375rem;
                border: 2px solid #e5e7eb;
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Validate dates whenever they change
    useEffect(() => {
        // Clear any previous errors when dates change
        setError(null);

        // Check that end date is not before start date
        if (tempStartDate && tempEndDate && tempEndDate < tempStartDate) {
            setError("End date cannot be before start date");
        }
    }, [tempStartDate, tempEndDate]);

    // Format date for display
    const formatDateForInput = (date: Date | null) => {
        return date ? format(date, "MM/dd/yyyy") : "";
    };

    // Custom input component with calendar icon
    const CustomInput = ({ value, onClick, placeholder, hasError }: any) => (
        <div className="relative w-full" onClick={onClick}>
            <input
                className={`w-full flex items-center p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8 ${hasError ? "border-red-500 ring-1 ring-red-500" : ""
                    }`}
                value={value}
                placeholder={placeholder}
                readOnly
            />
            <FaCalendarAlt className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500" />
        </div>
    );

    // Apply date filter with validation
    const applyDateFilter = () => {
        // Validate before applying
        if (tempStartDate && tempEndDate && tempEndDate < tempStartDate) {
            setError("End date cannot be before start date");
            return;
        }

        if (onChange) {
            onChange(tempStartDate, tempEndDate);
        }
        setIsOpen(false);
        setError(null);
    };

    // Clear date filter
    const clearDateFilter = () => {
        setTempStartDate(null);
        setTempEndDate(null);
        setError(null);
        if (onChange) {
            onChange(null, null);
        }
    };

    // Handle specific date changes with validation
    const handleStartDateChange = (date: Date | null) => {
        setTempStartDate(date);
    };

    const handleEndDateChange = (date: Date | null) => {
        setTempEndDate(date);
    };

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            {/* Main toggle button */}
            <button
                className={`className="w-64 h-12 bg-neutral-50 border border-neutral-200 hover:bg-white text-black font-semibold py-2 px-4 rounded-lg flex justify-center items-center"
 ${error ? "border-red-500 text-red-600" : ""
                    }`}
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
            >
                <span className="flex items-center">
                    {tempStartDate && tempEndDate
                        ? `${formatDateForInput(tempStartDate)} - ${formatDateForInput(tempEndDate)}`
                        : placeholder}
                </span>
                <div className="flex items-center">
                    <RxCaretDown
                        size={22}
                        className={`ml-2 transform transition-transform ${isOpen ? "rotate-180" : ""
                            }`}
                    />
                </div>
            </button>

            {/* Dropdown panel */}
            {isOpen && (
                <div className="absolute z-50 mt-1 bg-white border rounded shadow-lg p-4 w-80">
                    {/* Error display */}
                    {error && (
                        <div className="mb-4 p-2 bg-red-50 border border-red-200 text-red-600 text-sm rounded">
                            <strong>Error:</strong> {error}
                        </div>
                    )}

                    {/* Date inputs */}
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Start Date
                            </label>
                            <DatePicker
                                selected={tempStartDate}
                                onChange={handleStartDateChange}
                                selectsStart
                                startDate={tempStartDate}
                                endDate={tempEndDate}
                                customInput={
                                    <CustomInput
                                        placeholder="MM/DD/YYYY"
                                        hasError={!!error}
                                    />
                                }
                                dateFormat="MM/dd/yyyy"
                                popperClassName="z-50"
                                wrapperClassName="w-full"
                                maxDate={new Date()} // Optional: prevent future dates
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                End Date
                            </label>
                            <DatePicker
                                selected={tempEndDate}
                                onChange={handleEndDateChange}
                                selectsEnd
                                startDate={tempStartDate}
                                endDate={tempEndDate}
                                customInput={
                                    <CustomInput
                                        placeholder="MM/DD/YYYY"
                                        hasError={!!error}
                                    />
                                }
                                dateFormat="MM/dd/yyyy"
                                popperClassName="z-50"
                                wrapperClassName="w-full"
                            />
                        </div>
                    </div>

                    {/* Action buttons */}
                    <div className="mt-4 flex justify-between">
                        <button
                            className={`px-4 py-2 text-white rounded focus:outline-none ${error ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                                }`}
                            onClick={applyDateFilter}
                            disabled={!!error}
                        >
                            Apply
                        </button>
                        <button
                            className="px-4 py-2 text-black rounded bg-neutral-100 hover:bg-neutral-300 focus:outline-none"
                            onClick={clearDateFilter}
                        >
                            Clear
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};